import { getDoc, setDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import defaultConfig from '../config/landing.config';

const CONFIG_DOC_PATH = 'config/landing';
const generateWhatsAppLink = (phone, message) =>
    `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;


const safe = (obj, fallback) =>
  typeof obj === 'object' && obj !== null ? obj : fallback;

const oldPalette = new Set([
  '#f97316',
  '#ea580c',
  '#ffedd5',
  '#fff7ed',
  '#fcd34d',
  '#fb923c',
]);

const isLegacyContent = (text = '') =>
  typeof text === 'string' && /bbts|clover|tecnolog/i.test(text.toLowerCase());

const needsLegacyReset = (data = {}) =>
  isLegacyContent(data.siteTitle) ||
  isLegacyContent(data.siteName) ||
  isLegacyContent(data.hero?.title);

const needsThemeReset = (theme = {}) => {
  if (!theme) return false;
  return Object.values(theme).some(
    (value) => typeof value === 'string' && oldPalette.has(value.toLowerCase())
  );
};

export const getLandingConfig = async () => {
  try {
    const docRef = doc(db, CONFIG_DOC_PATH);
    const snap = await getDoc(docRef);

    if (snap.exists()) {
      const data = snap.data();

      const legacyReset = needsLegacyReset(data);
      const siteTitle = legacyReset
        ? defaultConfig.siteTitle
        : data.siteTitle || defaultConfig.siteTitle;
      const siteName = legacyReset
        ? defaultConfig.siteName
        : data.siteName || defaultConfig.siteName;
      const heroSource = safe(data.hero, {});
      const hasHeroImage = Object.prototype.hasOwnProperty.call(heroSource, 'image');
      const heroImage = hasHeroImage ? heroSource.image : defaultConfig.hero?.image || '';
      const hero = legacyReset
        ? {
            ...defaultConfig.hero,
            image: heroImage,
          }
        : {
            ...defaultConfig.hero,
            ...heroSource,
            image: heroImage,
          };
      const theme =
        legacyReset || needsThemeReset(data.theme)
          ? defaultConfig.theme
          : {
              ...(defaultConfig.theme || {}),
              ...safe(data.theme, {}),
            };
      const copy = {
        ...(defaultConfig.copy || {}),
        ...safe(data.copy, {}),
        header: {
          ...(defaultConfig.copy?.header || {}),
          ...safe(data.copy?.header, {}),
          markets: Array.isArray(data.copy?.header?.markets)
            ? data.copy.header.markets
            : defaultConfig.copy?.header?.markets || [],
        },
        hero: {
          ...(defaultConfig.copy?.hero || {}),
          ...safe(data.copy?.hero, {}),
        },
        servicesSection: {
          ...(defaultConfig.copy?.servicesSection || {}),
          ...safe(data.copy?.servicesSection, {}),
        },
        productSection: {
          ...(defaultConfig.copy?.productSection || {}),
          ...safe(data.copy?.productSection, {}),
        },
        measurementSection: {
          ...(defaultConfig.copy?.measurementSection || {}),
          ...safe(data.copy?.measurementSection, {}),
        },
        quoteStepsSection: {
          ...(defaultConfig.copy?.quoteStepsSection || {}),
          ...safe(data.copy?.quoteStepsSection, {}),
          taglines: Array.isArray(data.copy?.quoteStepsSection?.taglines)
            ? data.copy.quoteStepsSection.taglines
            : defaultConfig.copy?.quoteStepsSection?.taglines || [],
        },
        featuredProductsSection: {
          ...(defaultConfig.copy?.featuredProductsSection || {}),
          ...safe(data.copy?.featuredProductsSection, {}),
        },
        impactSection: {
          ...(defaultConfig.copy?.impactSection || {}),
          ...safe(data.copy?.impactSection, {}),
        },
        professionalSection: {
          ...(defaultConfig.copy?.professionalSection || {}),
          ...safe(data.copy?.professionalSection, {}),
        },
        testimonialSection: {
          ...(defaultConfig.copy?.testimonialSection || {}),
          ...safe(data.copy?.testimonialSection, {}),
        },
        whySection: {
          ...(defaultConfig.copy?.whySection || {}),
          ...safe(data.copy?.whySection, {}),
        },
        blogSection: {
          ...(defaultConfig.copy?.blogSection || {}),
          ...safe(data.copy?.blogSection, {}),
        },
        footerSection: {
          ...(defaultConfig.copy?.footerSection || {}),
          ...safe(data.copy?.footerSection, {}),
        },
        contactSection: {
          ...(defaultConfig.copy?.contactSection || {}),
          ...safe(data.copy?.contactSection, {}),
          labels: {
            ...(defaultConfig.copy?.contactSection?.labels || {}),
            ...safe(data.copy?.contactSection?.labels, {}),
          },
        },
        wizard: {
          ...(defaultConfig.copy?.wizard || {}),
          ...safe(data.copy?.wizard, {}),
          steps: Array.isArray(data.copy?.wizard?.steps)
            ? data.copy.wizard.steps
            : defaultConfig.copy?.wizard?.steps || [],
        },
        modals: {
          ...(defaultConfig.copy?.modals || {}),
          ...safe(data.copy?.modals, {}),
        },
        assistant: {
          ...(defaultConfig.copy?.assistant || {}),
          ...safe(data.copy?.assistant, {}),
        },
      };
      const wizardOptions = {
        ...(defaultConfig.wizardOptions || {}),
        ...safe(data.wizardOptions, {}),
        colors: Array.isArray(data.wizardOptions?.colors)
          ? data.wizardOptions.colors
          : defaultConfig.wizardOptions?.colors || [],
        glass: Array.isArray(data.wizardOptions?.glass)
          ? data.wizardOptions.glass
          : defaultConfig.wizardOptions?.glass || [],
        finish: Array.isArray(data.wizardOptions?.finish)
          ? data.wizardOptions.finish
          : defaultConfig.wizardOptions?.finish || [],
        series: Array.isArray(data.wizardOptions?.series)
          ? data.wizardOptions.series
          : defaultConfig.wizardOptions?.series || [],
      };

      return {
        ...defaultConfig,
        ...data,
        siteTitle,
        siteName,
        googleTagId: data.googleTagId || defaultConfig.googleTagId,
        hero,
        navigation: Array.isArray(data.navigation)
          ? data.navigation
          : defaultConfig.navigation,
        featuredProducts: Array.isArray(data.featuredProducts)
          ? data.featuredProducts
          : defaultConfig.featuredProducts,
        impactHighlights: Array.isArray(data.impactHighlights)
          ? data.impactHighlights
          : defaultConfig.impactHighlights,
        professionalSegments: Array.isArray(data.professionalSegments)
          ? data.professionalSegments
          : defaultConfig.professionalSegments,
        whyChoose: Array.isArray(data.whyChoose)
          ? data.whyChoose
          : defaultConfig.whyChoose,
        blogPosts: Array.isArray(data.blogPosts)
          ? data.blogPosts
          : defaultConfig.blogPosts,
        services: Array.isArray(data.services)
          ? data.services
          : defaultConfig.services,
        productCategories: Array.isArray(data.productCategories)
          ? data.productCategories
          : defaultConfig.productCategories,
        measurementGuidelines: Array.isArray(data.measurementGuidelines)
          ? data.measurementGuidelines
          : defaultConfig.measurementGuidelines,
        quoteSteps: Array.isArray(data.quoteSteps)
          ? data.quoteSteps
          : defaultConfig.quoteSteps,
          
          whatsappPhone: data.whatsappPhone || defaultConfig.whatsappPhone,
          whatsappMessage: data.whatsappMessage || defaultConfig.whatsappMessage,
          whatsappModal: {
            ...defaultConfig.whatsappModal,
            ...safe(data.whatsappModal, {}),
            link: generateWhatsAppLink(
              data.whatsappPhone || defaultConfig.whatsappPhone,
              data.whatsappMessage || defaultConfig.whatsappMessage
            ),
          },
        login: {
          ...defaultConfig.login,
          ...safe(data.login, {})
        },
        form: {
          ...defaultConfig.form,
          ...safe(data.form, {})
        },
        calendar: {
          ...defaultConfig.calendar,
          ...safe(data.calendar, {})
        },
        contactInfo: {
          ...defaultConfig.contactInfo,
          ...safe(data.contactInfo, {}),
          ctaCard: {
            ...(defaultConfig.contactInfo?.ctaCard || {}),
            ...safe(data.contactInfo?.ctaCard, {}),
          },
        },
        footer: {
          ...defaultConfig.footer,
          ...safe(data.footer, {}),
        },
        whatsappFloat: {
            link: generateWhatsAppLink(
              data.whatsappPhone || defaultConfig.whatsappPhone,
              data.whatsappMessage || defaultConfig.whatsappMessage
            )
          },
        animations: {
          ...defaultConfig.animations,
          ...safe(data.animations, {})
        },
        theme,
        copy,
        wizardOptions,
      };
    } else {
      await setDoc(docRef, defaultConfig);
      return defaultConfig;
    }
  } catch (err) {
    console.error('❌ Error cargando config desde Firebase:', err);
    return defaultConfig;
  }
};
